import React, {
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getGeoLocationLatLong } from "../../services/commonServices";
import {
  getContextFactor,
  expandBoundsForContext,
  getMapStyleRef,
} from "@/helpers/mapbox";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const removeMapLayer = (map, layerId) => {
  if (map.getLayer(layerId)) map.removeLayer(layerId);
};

const removeMapSource = (map, sourceId) => {
  if (map.getSource(sourceId)) map.removeSource(sourceId);
};

const fitPolygonBounds = (map, formattedCoordinates) => {
  const bounds = new mapboxgl.LngLatBounds();
  formattedCoordinates.forEach((coords) => {
    coords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
  });
  if (!bounds.isEmpty()) {
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const [minLng, minLat, maxLng, maxLat] = expandBoundsForContext([
      sw.lng,
      sw.lat,
      ne.lng,
      ne.lat,
    ]);
    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: { top: 60, bottom: 60, left: 40, right: 40 },
        maxZoom: 13,
        duration: 1000,
      },
    );
  }
};

const fitCircleBounds = (map, centerCoords, radiusM) => {
  const [centerLng, centerLat] = centerCoords;
  const kmPerDegreeLat = 111.32;
  const kmPerDegreeLng = 111.32 * Math.cos((centerLat * Math.PI) / 180);
  const radiusKm = radiusM / 1000;
  const factor = getContextFactor(radiusM / 1609.34);
  const offsetLat = (radiusKm / kmPerDegreeLat) * factor;
  const offsetLng = (radiusKm / kmPerDegreeLng) * factor;
  map.fitBounds(
    [
      [centerLng - offsetLng, centerLat - offsetLat],
      [centerLng + offsetLng, centerLat + offsetLat],
    ],
    { padding: 60, duration: 1000 },
  );
};

const formatPolygonCoordinates = (polygonCoordinates) =>
  polygonCoordinates.map((polygon) =>
    polygon[0].map((point) => [point.lng, point.lat]),
  );

const clearDrawLayers = (map) => {
  removeMapLayer(map, "draw-line-layer");
  removeMapSource(map, "draw-line");
  removeMapLayer(map, "drawn-polygon-fill");
  removeMapLayer(map, "drawn-polygon-outline");
  removeMapSource(map, "drawn-polygon");
};

const DRAW_HINT_STYLE = {
  position: "absolute",
  top: "10px",
  left: "50%",
  transform: "translateX(-50%)",
  padding: "8px 16px",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  color: "#fff",
  borderRadius: "4px",
  fontSize: "14px",
  zIndex: 1,
  pointerEvents: "none",
};

const MapBoxComponent = forwardRef(
  (
    {
      center: centerProp,
      zoom = 10,
      radiusMiles,
      height = "400px",
      width = "100%",
      onRadiusUpdate,
      handleItemSelection,
      polygonCoordinates,
      showPolygon = false,
      isEditable = false,
      getDetailedAddress = false,
      drawMode = false,
      onDrawComplete,
      showResetButton = true,
    },
    ref,
  ) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const mapLoadedRef = useRef(false);
    const drawHandlersRef = useRef(null);
    const isDrawingRef = useRef(false);
    const drawCoordsRef = useRef([]);
    const [hasDrawnPolygon, setHasDrawnPolygon] = useState(false);

    const DEFAULT_CENTER = [-112.074, 33.4484]; // Phoenix, AZ
    const center = useMemo(() => {
      if (!centerProp || centerProp.length < 2) return DEFAULT_CENTER;
      const [lng, lat] = centerProp.map(Number);
      if (!lng || !lat || isNaN(lng) || isNaN(lat)) return DEFAULT_CENTER;
      return [lng, lat];
    }, [centerProp]);
    const numericCenter = center;
    const [currentCenter, setCurrentCenter] = useState(numericCenter);
    const radiusMeters = useMemo(
      () => (radiusMiles ? radiusMiles * 1609.34 : 0),
      [radiusMiles],
    );


    const propsRef = useRef();
    propsRef.current = {
      currentCenter,
      radiusMeters,
      radiusMiles,
      isEditable,
      getDetailedAddress,
      handleItemSelection,
      onRadiusUpdate,
      showPolygon,
      polygonCoordinates,
      zoom,
      drawMode,
      onDrawComplete,
    };

    useImperativeHandle(ref, () => ({
      canvas: () => mapRef.current?.getCanvas(),
      map: () => mapRef.current,
    }));

    const clearVisuals = (map) => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      removeMapLayer(map, "circle-layer");
      removeMapLayer(map, "circle-outline-layer");
      removeMapSource(map, "circle");
      removeMapLayer(map, "polygon-layer");
      removeMapLayer(map, "polygon-outline-layer");
      removeMapSource(map, "polygon");
    };

    const buildCirclePolygon = (centerCoords, radiusM) => {
      const steps = 64;
      const [centerLng, centerLat] = centerCoords;
      const kmPerDegreeLat = 111.32;
      const kmPerDegreeLng = 111.32 * Math.cos((centerLat * Math.PI) / 180);
      const radiusKm = radiusM / 1000;
      const coords = [];
      for (let i = 0; i < steps; i++) {
        const angle = (i * 360) / steps;
        const rad = (angle * Math.PI) / 180;
        const dx = radiusKm * Math.cos(rad);
        const dy = radiusKm * Math.sin(rad);
        coords.push([
          centerLng + dx / kmPerDegreeLng,
          centerLat + dy / kmPerDegreeLat,
        ]);
      }
      coords.push(coords[0]);
      return coords;
    };

    const addCircle = (map, centerCoords, radiusM) => {
      const ring = buildCirclePolygon(centerCoords, radiusM);

      map.addSource("circle", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [ring] },
        },
      });

      map.addLayer({
        id: "circle-layer",
        type: "fill",
        source: "circle",
        paint: {
          "fill-color": "#007bff",
          "fill-opacity": 0.1,
        },
      });

      map.addLayer({
        id: "circle-outline-layer",
        type: "line",
        source: "circle",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
        },
      });
    };

    const addPolygon = (map, polyCoords) => {
      const formatted = formatPolygonCoordinates(polyCoords);

      map.addSource("polygon", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: formatted.map((coordinates) => ({
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [coordinates] },
          })),
        },
      });

      map.addLayer({
        id: "polygon-layer",
        type: "fill",
        source: "polygon",
        paint: {
          "fill-color": "rgba(0, 123, 255, 0.5)",
          "fill-opacity": 0.4,
        },
      });

      map.addLayer({
        id: "polygon-outline-layer",
        type: "line",
        source: "polygon",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
        },
      });

      fitPolygonBounds(map, formatted);
    };

    const addMarker = (map, centerCoords) => {
      const props = propsRef.current;
      const marker = new mapboxgl.Marker({ draggable: props.isEditable })
        .setLngLat(centerCoords)
        .addTo(map);

      markerRef.current = marker;

      if (props.isEditable) {
        marker.on("dragend", async () => {
          const lngLat = marker.getLngLat();
          const draggedCenter = [lngLat.lng, lngLat.lat];
          const currentProps = propsRef.current;

          setCurrentCenter(draggedCenter);

          const { addressObject } = await getGeoLocationLatLong(
            lngLat.lat,
            lngLat.lng,
            currentProps.getDetailedAddress,
          );
          currentProps.handleItemSelection(addressObject);
        });
      }

      if (props.radiusMiles) {
        addCircle(map, centerCoords, props.radiusMeters);
        fitCircleBounds(map, centerCoords, props.radiusMeters);
      }
    };

    const initializeDraw = (map) => {
      removeDraw(map);

      map.addSource("draw-line", {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: { type: "LineString", coordinates: [] },
        },
      });

      map.addLayer({
        id: "draw-line-layer",
        type: "line",
        source: "draw-line",
        paint: {
          "line-color": "#007bff",
          "line-width": 2,
          "line-dasharray": [2, 2],
        },
      });

      const onMouseDown = (e) => {
        if (e.originalEvent.button !== 0) return;
        isDrawingRef.current = true;
        drawCoordsRef.current = [[e.lngLat.lng, e.lngLat.lat]];
        map.dragPan.disable();
      };

      const onMouseMove = (e) => {
        if (!isDrawingRef.current) return;
        drawCoordsRef.current.push([e.lngLat.lng, e.lngLat.lat]);
        map.getSource("draw-line")?.setData({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: drawCoordsRef.current,
          },
        });
      };

      const onMouseUp = () => {
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;
        map.dragPan.enable();

        const coords = drawCoordsRef.current;
        if (coords.length < 3) return;

        const closedCoords = [...coords, coords[0]];

        removeMapLayer(map, "draw-line-layer");
        removeMapSource(map, "draw-line");

        map.addSource("drawn-polygon", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [closedCoords] },
          },
        });

        map.addLayer({
          id: "drawn-polygon-fill",
          type: "fill",
          source: "drawn-polygon",
          paint: {
            "fill-color": "rgba(0, 123, 255, 0.3)",
            "fill-opacity": 0.5,
          },
        });

        map.addLayer({
          id: "drawn-polygon-outline",
          type: "line",
          source: "drawn-polygon",
          paint: {
            "line-color": "#007bff",
            "line-width": 2,
          },
        });

        const bounds = new mapboxgl.LngLatBounds();
        closedCoords.forEach(([lng, lat]) => bounds.extend([lng, lat]));
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, { padding: 50, duration: 500 });
        }

        map.off("mousedown", onMouseDown);
        map.off("mousemove", onMouseMove);
        map.off("mouseup", onMouseUp);
        drawHandlersRef.current = null;

        const formattedCoords = closedCoords.map(([lng, lat]) => ({
          lng,
          lat,
        }));
        setHasDrawnPolygon(true);
        propsRef.current.onDrawComplete?.(formattedCoords);
      };

      drawHandlersRef.current = { onMouseDown, onMouseMove, onMouseUp };
      map.on("mousedown", onMouseDown);
      map.on("mousemove", onMouseMove);
      map.on("mouseup", onMouseUp);

      map.getCanvas().style.cursor = "crosshair";
    };

    const removeDraw = (map) => {
      if (drawHandlersRef.current) {
        map.off("mousedown", drawHandlersRef.current.onMouseDown);
        map.off("mousemove", drawHandlersRef.current.onMouseMove);
        map.off("mouseup", drawHandlersRef.current.onMouseUp);
        drawHandlersRef.current = null;
      }
      isDrawingRef.current = false;
      drawCoordsRef.current = [];
      clearDrawLayers(map);
      map.getCanvas().style.cursor = "";
      if (!map.dragPan._enabled) {
        map.dragPan.enable();
      }
    };

    const handleClearDraw = () => {
      const map = mapRef.current;
      if (!map) return;

      clearDrawLayers(map);
      setHasDrawnPolygon(false);
      initializeDraw(map);
    };

    const updateMapContent = () => {
      const map = mapRef.current;
      if (!map || !mapLoadedRef.current || !map.isStyleLoaded()) return;

      const props = propsRef.current;

      if (props.drawMode) {
        clearVisuals(map);
        if (!drawHandlersRef.current && !hasDrawnPolygon) {
          initializeDraw(map);
        }
        return;
      }

      removeDraw(map);
      setHasDrawnPolygon(false);
      clearVisuals(map);

      if (props.showPolygon && props.polygonCoordinates?.length > 0) {
        addPolygon(map, props.polygonCoordinates);
      } else {
        addMarker(map, props.currentCenter);
        if (!props.radiusMiles) {
          map.flyTo({ center: props.currentCenter, zoom: props.zoom });
        }
      }

      if (props.onRadiusUpdate && props.radiusMiles) {
        props.onRadiusUpdate(props.radiusMiles);
      }
    };

    useEffect(() => {
      if (!mapContainerRef.current) return;

      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: getMapStyleRef().url,
        center: numericCenter,
        zoom,
        attributionControl: false,
        preserveDrawingBuffer: true,
      });

      mapRef.current = map;

      map.on("load", () => {
        mapLoadedRef.current = true;
        updateMapContent();
      });

      return () => {
        mapLoadedRef.current = false;
        if (drawHandlersRef.current && map) {
          removeDraw(map);
        }
        mapRef.current = null;
        markerRef.current = null;
        map.remove();
      };
    }, []);

    useEffect(() => {
      const container = mapContainerRef.current;
      if (!container || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => {
        mapRef.current?.resize();
      });
      observer.observe(container);
      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      updateMapContent();
    }, [currentCenter, radiusMiles, showPolygon, polygonCoordinates, drawMode]);

    useEffect(() => {
      const newCenter = center.map(Number);
      if (
        !showPolygon &&
        (newCenter[0] !== currentCenter[0] || newCenter[1] !== currentCenter[1])
      ) {
        setCurrentCenter(newCenter);
      }
    }, [center, showPolygon]);

    const handleReset = () => {
      const map = mapRef.current;
      if (!map) return;

      if (showPolygon && polygonCoordinates?.length > 0) {
        fitPolygonBounds(map, formatPolygonCoordinates(polygonCoordinates));
      } else if (radiusMiles) {
        fitCircleBounds(map, currentCenter, radiusMeters);
      } else {
        map.flyTo({ center: currentCenter, zoom });
      }
    };

    return (
      <div
        style={{ position: "relative", height, width, overflow: "hidden" }}
        id="mapboxId"
        className="h-100"
      >
        <div
          ref={mapContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
          }}
        ></div>

        {drawMode && !hasDrawnPolygon && (
          <div style={DRAW_HINT_STYLE}>Click and drag to draw your area</div>
        )}

        {drawMode && hasDrawnPolygon && (
          <button onClick={handleClearDraw} className="map-control-btn">
            Clear & Redraw
          </button>
        )}

        {!drawMode && showResetButton && (
          <button
            onClick={handleReset}
            className="map-control-btn"
            id="mapboxResetButton"
          >
            Reset Map
          </button>
        )}
      </div>
    );
  },
);

export default memo(MapBoxComponent);
